/**
 * DecisionFlow — MultiYearManager
 * ui/src/components/tenant-admin/MultiYearManager.jsx
 *
 * Domain: §P4-04 Multi-Year Deals
 * Embedded in: PlanDetailView modifier card grid (Pattern A)
 * API:
 *   GET  /api/v1/multi-year-deal-policies?plan_component_id={id}
 *   POST /api/v1/multi-year-deal-policies
 *   POST /api/v1/multi-year-deal-policies/{id}/approve
 *   GET  /api/v1/multi-year-deal-schedules?policy_id={id}
 *   POST /api/v1/multi-year-deal-schedules
 * Feature gate: Enterprise — multi_year_deals
 *   403 FEATURE_UNAVAILABLE → GateStatusBanner, no content rendered.
 * Tabs: Policies | Schedules
 * State derived from approved_by / submitted_at — no status column.
 * planComponentId prop is required — all API calls scoped to it.
 * Path: ui/src/components/tenant-admin/MultiYearManager.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getApiErrorCode(error) { return error?.code || null; }
function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// CSS tokens
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'policies',  label: 'Policies'  },
  { key: 'schedules', label: 'Schedules' },
];

// ---------------------------------------------------------------------------
// Form defaults
// ---------------------------------------------------------------------------

const EMPTY_POLICY = {
  policy_name:          '',
  max_deal_years:       '',
  year1_rate_pct:       '',
  renewal_rate_pct:     '',
  effective_date:       '',
  expiration_date:      '',
};

const EMPTY_SCHEDULE = {
  deal_year:            '',
  payout_rate_pct:      '',
  accelerator_pct:      '',
};

// ---------------------------------------------------------------------------
// MultiYearManager
// ---------------------------------------------------------------------------

export default function MultiYearManager({
  mode, period, wizardSteps, embedded = false,
  planId, planComponentId, fiscalPeriodId, readOnly = false,
}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.multi_year')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  const [gated, setGated]                   = useState(false);
  const [activeTab, setActiveTab]           = useState('policies');

  // Policies tab state
  const [policies, setPolicies]             = useState([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [policiesError, setPoliciesError]   = useState('');
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showPolicyForm, setShowPolicyForm] = useState(false);
  const [policyForm, setPolicyForm]         = useState(EMPTY_POLICY);
  const [policySaving, setPolicySaving]     = useState(false);
  const [policyFormError, setPolicyFormError] = useState('');
  const [actionLoading, setActionLoading]   = useState(false);
  const [actionError, setActionError]       = useState('');

  // Schedules tab state
  const [schedules, setSchedules]           = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [schedulesError, setSchedulesError] = useState('');
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleForm, setScheduleForm]     = useState(EMPTY_SCHEDULE);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleFormError, setScheduleFormError] = useState('');

  const [toast, setToast]                   = useState(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  // ---------------------------------------------------------------------------
  // Load policies — enterprise gate check first
  // ---------------------------------------------------------------------------

  const loadPolicies = useCallback(async () => {
    if (!user || !planComponentId) return;
    setPoliciesLoading(true);
    setPoliciesError('');
    setGated(false);
    try {
      const data = await apiGet(`/api/v1/multi-year-deal-policies?plan_component_id=${planComponentId}`);
      setPolicies(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'FEATURE_UNAVAILABLE') {
        setGated(true);
      } else {
        setPoliciesError(getErrorMessage(error, 'Unable to load multi-year deal policies.'));
      }
    } finally {
      setPoliciesLoading(false);
    }
  }, [user, planComponentId]);

  useEffect(() => { loadPolicies(); }, [loadPolicies]);

  // ---------------------------------------------------------------------------
  // Load schedules for selected policy
  // ---------------------------------------------------------------------------

  const loadSchedules = useCallback(async (policyId) => {
    if (!policyId) return;
    setSchedulesLoading(true);
    setSchedulesError('');
    try {
      const data = await apiGet(`/api/v1/multi-year-deal-schedules?policy_id=${policyId}`);
      setSchedules(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      setSchedulesError(getErrorMessage(error, 'Unable to load schedules.'));
    } finally {
      setSchedulesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'schedules' && selectedPolicy) {
      loadSchedules(selectedPolicy.multi_year_deal_policy_id);
    }
  }, [activeTab, selectedPolicy, loadSchedules]);

  // ---------------------------------------------------------------------------
  // Create policy
  // ---------------------------------------------------------------------------

  async function handleCreatePolicy(e) {
    e.preventDefault();
    if (!policyForm.policy_name.trim() || !policyForm.max_deal_years) {
      setPolicyFormError('Policy name and max deal years are required.');
      return;
    }
    setPolicySaving(true);
    setPolicyFormError('');
    try {
      await apiPost('/api/v1/multi-year-deal-policies', {
        plan_component_id: planComponentId,
        policy_name:       policyForm.policy_name.trim(),
        max_deal_years:    parseInt(policyForm.max_deal_years, 10),
        year1_rate_pct:    policyForm.year1_rate_pct ? parseFloat(policyForm.year1_rate_pct) : null,
        renewal_rate_pct:  policyForm.renewal_rate_pct ? parseFloat(policyForm.renewal_rate_pct) : null,
        effective_date:    policyForm.effective_date || null,
        expiration_date:   policyForm.expiration_date || null,
      });
      setShowPolicyForm(false);
      setPolicyForm(EMPTY_POLICY);
      showToast('Policy created.');
      await loadPolicies();
    } catch (error) {
      setPolicyFormError(getErrorMessage(error, 'Failed to create policy.'));
    } finally {
      setPolicySaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Submit / Approve policy
  // ---------------------------------------------------------------------------

  async function handleSubmit() {
    if (!selectedPolicy) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/multi-year-deal-policies/${selectedPolicy.multi_year_deal_policy_id}/submit`);
      showToast('Submitted for approval.');
      await loadPolicies();
      setSelectedPolicy((prev) => ({ ...prev, submitted_at: new Date().toISOString() }));
    } catch (error) {
      setActionError(getErrorMessage(error, 'Failed to submit for approval.'));
    } finally {
      setActionLoading(false);
    }
  }

  async function handleApprove() {
    if (!selectedPolicy) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/multi-year-deal-policies/${selectedPolicy.multi_year_deal_policy_id}/approve`);
      showToast('Policy approved.');
      await loadPolicies();
      setSelectedPolicy((prev) => ({ ...prev, approved_by: user?.user_id || user?.id }));
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_NOT_ALLOWED') {
        setActionError('Self-approval is not permitted (NN-10).');
      } else {
        setActionError(getErrorMessage(error, 'Failed to approve.'));
      }
    } finally {
      setActionLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Create schedule
  // ---------------------------------------------------------------------------

  async function handleCreateSchedule(e) {
    e.preventDefault();
    if (!selectedPolicy) {
      setScheduleFormError('Select a policy first.');
      return;
    }
    if (!scheduleForm.deal_year || !scheduleForm.payout_rate_pct) {
      setScheduleFormError('Deal year and payout rate are required.');
      return;
    }
    setScheduleSaving(true);
    setScheduleFormError('');
    try {
      await apiPost('/api/v1/multi-year-deal-schedules', {
        multi_year_deal_policy_id: selectedPolicy.multi_year_deal_policy_id,
        deal_year:                 parseInt(scheduleForm.deal_year, 10),
        payout_rate_pct:           parseFloat(scheduleForm.payout_rate_pct),
        accelerator_pct:           scheduleForm.accelerator_pct ? parseFloat(scheduleForm.accelerator_pct) : null,
      });
      setShowScheduleForm(false);
      setScheduleForm(EMPTY_SCHEDULE);
      showToast('Schedule row added.');
      await loadSchedules(selectedPolicy.multi_year_deal_policy_id);
    } catch (error) {
      setScheduleFormError(getErrorMessage(error, 'Failed to add schedule row.'));
    } finally {
      setScheduleSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  const currentUserId = user?.user_id || user?.id || null;

  if (!user) {
    return (
      <section className="flex flex-col gap-4">
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  if (gated) {
    return (
      <section className="flex flex-col gap-4">
        <GateStatusBanner feature="multi_year_deals" />
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-4">

      {readOnly && (
        <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Plan is approved — configuration is read-only.
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))]">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-tok-sm font-medium border-b-2 transition ${
              activeTab === t.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Policies tab ── */}
      {activeTab === 'policies' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Multi-Year Deal Policies</h2>
            {!readOnly && (
              <button
                type="button"
                onClick={() => { setShowPolicyForm((v) => !v); setPolicyFormError(''); setPolicyForm(EMPTY_POLICY); }}
                className={btnPrimary}
              >
                {showPolicyForm ? 'Cancel' : '+ Add Policy'}
              </button>
            )}
          </div>

          {showPolicyForm && (
            <form
              onSubmit={handleCreatePolicy}
              className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3"
            >
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Policy</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className={labelClass + ' sm:col-span-2'}>
                  Policy Name <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input
                    type="text"
                    value={policyForm.policy_name}
                    onChange={(e) => setPolicyForm((f) => ({ ...f, policy_name: e.target.value }))}
                    placeholder="e.g. Standard Multi-Year"
                    className={inputClass}
                    required
                  />
                </label>
                <label className={labelClass}>
                  Max Deal Years <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    step="1"
                    value={policyForm.max_deal_years}
                    onChange={(e) => setPolicyForm((f) => ({ ...f, max_deal_years: e.target.value }))}
                    placeholder="3"
                    className={inputClass}
                    required
                  />
                </label>
                <label className={labelClass}>
                  Year 1 Rate %
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={policyForm.year1_rate_pct}
                    onChange={(e) => setPolicyForm((f) => ({ ...f, year1_rate_pct: e.target.value }))}
                    placeholder="0.00"
                    className={inputClass}
                  />
                </label>
                <label className={labelClass}>
                  Renewal Rate %
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={policyForm.renewal_rate_pct}
                    onChange={(e) => setPolicyForm((f) => ({ ...f, renewal_rate_pct: e.target.value }))}
                    placeholder="0.00"
                    className={inputClass}
                  />
                </label>
                <label className={labelClass}>
                  Effective Date
                  <input
                    type="date"
                    value={policyForm.effective_date}
                    onChange={(e) => setPolicyForm((f) => ({ ...f, effective_date: e.target.value }))}
                    className={inputClass}
                  />
                </label>
                <label className={labelClass}>
                  Expiration Date
                  <input
                    type="date"
                    value={policyForm.expiration_date}
                    onChange={(e) => setPolicyForm((f) => ({ ...f, expiration_date: e.target.value }))}
                    className={inputClass}
                  />
                </label>
              </div>
              {policyFormError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                  {policyFormError}
                </div>
              )}
              <div className="flex gap-2">
                <button type="submit" disabled={policySaving} className={btnPrimary}>
                  {policySaving ? 'Saving…' : 'Create Policy'}
                </button>
                <button type="button" onClick={() => { setShowPolicyForm(false); setPolicyFormError(''); }} className={btnSecondary}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {policiesLoading && (
            <div className="animate-pulse space-y-2">
              <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
              <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
            </div>
          )}
          {policiesError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {policiesError}
            </div>
          )}
          {!policiesLoading && !policiesError && policies.length === 0 && (
            <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No multi-year deal policies</p>
              <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Add a policy to configure multi-year deal compensation.</p>
            </div>
          )}
          {policies.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Policy Name</th>
                    <th className={thClass}>Max Years</th>
                    <th className={thClass}>Year 1 Rate</th>
                    <th className={thClass}>Renewal Rate</th>
                    <th className={thClass}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {policies.map((p) => (
                    <tr
                      key={p.multi_year_deal_policy_id}
                      className={`cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors ${selectedPolicy?.multi_year_deal_policy_id === p.multi_year_deal_policy_id ? 'bg-[rgb(var(--color-primary))]/5' : ''}`}
                      onClick={() => { setSelectedPolicy(p); setActionError(''); }}
                    >
                      <td className={tdClass + ' font-medium'}>{p.policy_name}</td>
                      <td className={tdClass}>{p.max_deal_years ?? '—'}</td>
                      <td className={tdClass}>{p.year1_rate_pct != null ? `${p.year1_rate_pct}%` : '—'}</td>
                      <td className={tdClass}>{p.renewal_rate_pct != null ? `${p.renewal_rate_pct}%` : '—'}</td>
                      <td className={tdClass}><StatusPill status={deriveState(p)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Approval panel for selected policy */}
          {selectedPolicy && !readOnly && (() => {
            const state = deriveState(selectedPolicy);
            return (
              <div className="flex flex-col gap-3">
                <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Selected: <strong className="text-[rgb(var(--color-text))]">{selectedPolicy.policy_name}</strong>
                </div>
                {actionError && (
                  <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                    {actionError}
                  </div>
                )}
                <ApprovalPanel
                  variant="multi_year_deal_policy"
                  currentState={state}
                  submittedBy={selectedPolicy.submitted_by}
                  currentUserId={currentUserId}
                  onSubmit={state === 'draft' ? handleSubmit : null}
                  onApprove={state === 'pending_approval' ? handleApprove : null}
                  onReject={null}
                  approvedBy={selectedPolicy.approved_by}
                  approvedAt={selectedPolicy.approved_at}
                  loading={actionLoading}
                />
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Schedules tab ── */}
      {activeTab === 'schedules' && (
        <div className="flex flex-col gap-4">
          {!selectedPolicy && (
            <div className="rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
              Select a policy in the Policies tab first to manage its schedule rows.
            </div>
          )}

          {selectedPolicy && (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Year Schedules</h2>
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Policy: {selectedPolicy.policy_name}</p>
                </div>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => { setShowScheduleForm((v) => !v); setScheduleFormError(''); setScheduleForm(EMPTY_SCHEDULE); }}
                    className={btnPrimary}
                  >
                    {showScheduleForm ? 'Cancel' : '+ Add Year'}
                  </button>
                )}
              </div>

              {showScheduleForm && (
                <form
                  onSubmit={handleCreateSchedule}
                  className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3"
                >
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">Add Schedule Row</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <label className={labelClass}>
                      Deal Year <span className="text-[rgb(var(--color-danger))]">*</span>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        step="1"
                        value={scheduleForm.deal_year}
                        onChange={(e) => setScheduleForm((f) => ({ ...f, deal_year: e.target.value }))}
                        placeholder="1"
                        className={inputClass}
                        required
                      />
                    </label>
                    <label className={labelClass}>
                      Payout Rate % <span className="text-[rgb(var(--color-danger))]">*</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={scheduleForm.payout_rate_pct}
                        onChange={(e) => setScheduleForm((f) => ({ ...f, payout_rate_pct: e.target.value }))}
                        placeholder="0.00"
                        className={inputClass}
                        required
                      />
                    </label>
                    <label className={labelClass}>
                      Accelerator %
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={scheduleForm.accelerator_pct}
                        onChange={(e) => setScheduleForm((f) => ({ ...f, accelerator_pct: e.target.value }))}
                        placeholder="0.00"
                        className={inputClass}
                      />
                    </label>
                  </div>
                  {scheduleFormError && (
                    <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                      {scheduleFormError}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button type="submit" disabled={scheduleSaving} className={btnPrimary}>
                      {scheduleSaving ? 'Saving…' : 'Add Row'}
                    </button>
                    <button type="button" onClick={() => { setShowScheduleForm(false); setScheduleFormError(''); }} className={btnSecondary}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {schedulesLoading && (
                <div className="animate-pulse space-y-2">
                  <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
                </div>
              )}
              {schedulesError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {schedulesError}
                </div>
              )}
              {!schedulesLoading && !schedulesError && schedules.length === 0 && (
                <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No schedule rows. Add year-by-year rates above.</p>
                </div>
              )}
              {schedules.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Deal Year</th>
                        <th className={thClass}>Payout Rate %</th>
                        <th className={thClass}>Accelerator %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedules.map((s, i) => (
                        <tr key={s.schedule_id ?? i} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass + ' font-medium'}>Year {s.deal_year}</td>
                          <td className={tdClass}>{s.payout_rate_pct != null ? `${s.payout_rate_pct}%` : '—'}</td>
                          <td className={tdClass}>{s.accelerator_pct != null ? `${s.accelerator_pct}%` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-4 right-4 z-50 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] shadow-lg"
        >
          {toast}
        </div>
      )}
    </section>
  );
}
